import React, { useEffect, useRef, useState } from 'react';
import { createStockItem, createStockList, getStockLists, getImportProfiles, getImportConversions, getProviders } from '../../api';
import { normalizePaginatedResponse } from '../../utils/pagination';
import { calculateStockAmounts } from '../../utils/stockCost';
import { isStockPack, stockPackTotal } from '../../utils/stockPack';
import { getStandardStockPurchaseGuidance, getStockInputScope, isStandardGranelPurchase, resolveStandardStockQuantity, stockPurchaseUnitLabel, STOCK_AMOUNT_GUIDANCE } from '../../utils/stockEntryGuidance';
import { buildStockEntryPayload, getMissingStockConversions, getStockSaleUnits, STOCK_UNIT_OPTIONS } from '../../utils/stockEntryForm';
import { getPurchasePackFormats, selectStockPurchase } from '../../utils/stockPurchaseFormat';
import StockPurchaseFormatSelector from '../StockPurchaseFormatSelector';
import StockCostPerKgPreview from '../StockCostPerKgPreview';
import VarietyEditorDialog from '../varietyPricing/VarietyEditorDialog';
import StockCostReferenceSummary, { costAvailabilityMessage, costNumber, referenceMoney } from './StockCostReferenceSummary';

const NEW_LIST = '__new__';
const EMPTY_DRAFT = { purchaseMode: '', purchaseVarietyPriceId: null, price: '', priceWithoutIva: '', quantity: '', metricUnit: '', weight: '', providerId: '', bultosPerBin: '', unitsPerPack: '', lengthCm: '', widthCm: '', heightCm: '' };
const errorMessage = (error) => error?.response?.data?.message || error?.message || 'No se pudo guardar el ingreso de stock.';
const listTypeLabel = (type) => type === 'importaciones' ? 'Importaciones' : 'Normal';
const listLabel = (list) => `${list.name} · ${listTypeLabel(list.listType)} · ${list.status === 'completed' ? 'Completada' : 'Borrador'}`;
const inputClass = 'mt-1 block w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm disabled:bg-slate-100';

export default function AddStockInputDialog({ branchId, product, variety, format, canCreateStock, canReadProviders = false, isCurrent = () => true, onSaved, onClose }) {
  const [lists, setLists] = useState([]);
  const [providers, setProviders] = useState([]);
  const [listId, setListId] = useState('');
  const [listName, setListName] = useState('');
  const [newListType, setNewListType] = useState('standard');
  const [draft, setDraft] = useState(() => selectStockPurchase(EMPTY_DRAFT, `pack:${format.id}`, variety.formats || [format]));
  const automaticPackSelection = useRef(draft.purchaseMode === 'pack');
  const [conversions, setConversions] = useState([]);
  const [conversionInputs, setConversionInputs] = useState({});
  const [loading, setLoading] = useState(true);
  const [loadingDefaults, setLoadingDefaults] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [defaultsError, setDefaultsError] = useState('');
  const [providerError, setProviderError] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [reload, setReload] = useState(0);
  const [defaultsReload, setDefaultsReload] = useState(0);
  const [savedStock, setSavedStock] = useState(null);
  const [refreshedFormat, setRefreshedFormat] = useState(null);
  const mounted = useRef(false);
  const operation = useRef(false);
  const touched = useRef(new Set());
  const resultRef = useRef(null);
  const [initialCosts] = useState(() => JSON.parse(JSON.stringify(format.costs || {})));
  const selectedList = lists.find((list) => String(list.id) === listId);
  const listType = listId === NEW_LIST ? newListType : selectedList?.listType || 'standard';
  const isImportList = listType === 'importaciones';
  const isResaleOnly = Boolean(product.category?.isResaleOnly || product.Category?.isResaleOnly);
  const saleUnits = getStockSaleUnits(variety.formats || [format]);
  const missingConversions = isImportList && !isResaleOnly
    ? getMissingStockConversions({ metricUnit: draft.metricUnit, saleUnits, conversions }) : [];
  const granel = isStandardGranelPurchase({ isImportList, purchaseMode: isImportList ? null : draft.purchaseMode, metricUnit: draft.metricUnit });
  const guidance = getStandardStockPurchaseGuidance({ isImportList, purchaseMode: isImportList ? null : draft.purchaseMode, metricUnit: draft.metricUnit });
  const disabled = busy || loading || loadingDefaults || !canCreateStock || Boolean(savedStock);
  const validContext = () => mounted.current && isCurrent();

  useEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; };
  }, []);

  useEffect(() => {
    if (savedStock) resultRef.current?.focus();
  }, [savedStock, refreshedFormat]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setLoadError('');
    setProviderError('');
    Promise.all([
      getStockLists(branchId, undefined, undefined, { pageSize: 0 }),
      canReadProviders ? getProviders({ pageSize: 0 }).then((result) => ({ result }), (failure) => ({ failure })) : Promise.resolve({ result: [] }),
    ]).then(([response, providerResponse]) => {
      if (!active) return;
      const available = normalizePaginatedResponse(response).data.filter((list) => !list.branchId || String(list.branchId) === String(branchId));
      setLists((current) => [...available, ...current.filter((list) => !available.some((entry) => String(entry.id) === String(list.id)))]);
      if (!available.length) setListId((current) => current || NEW_LIST);
      setProviders(normalizePaginatedResponse(providerResponse.result).data.filter((provider) => !provider.isDeleted && provider.isActive !== false));
      if (providerResponse.failure) setProviderError('No se pudieron cargar los proveedores.');
    }).catch((failure) => { if (active) setLoadError(errorMessage(failure)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [branchId, canReadProviders, reload]);

  useEffect(() => {
    let active = true;
    setDefaultsError('');
    setConversions([]);
    setConversionInputs({});
    if (!isImportList) { setLoadingDefaults(false); return undefined; }
    setLoadingDefaults(true);
    Promise.all([getImportProfiles(branchId, [variety.id]), getImportConversions(branchId, [variety.id])])
      .then(([profiles, savedConversions]) => {
        if (!active) return;
        const profile = normalizePaginatedResponse(profiles).data.find((entry) => String(entry.varietyId) === String(variety.id));
        if (profile) setDraft((current) => {
          const next = { ...current };
          Object.keys(EMPTY_DRAFT).forEach((field) => {
            const value = field === 'weight' ? profile.weightKg : profile[field];
            if (!touched.current.has(field) && value !== null && value !== undefined) next[field] = String(value);
          });
          return next;
        });
        setConversions(normalizePaginatedResponse(savedConversions).data);
      }).catch((failure) => { if (active) setDefaultsError(errorMessage(failure)); })
      .finally(() => { if (active) setLoadingDefaults(false); });
    return () => { active = false; };
  }, [branchId, variety.id, isImportList, defaultsReload]);

  const asGranelPurchase = (current) => {
    const next = { ...current, quantity: '1' };
    const multiplier = Number(current.quantity) > 0 ? Number(current.quantity) : 1;
    ['price', 'priceWithoutIva', 'weight'].forEach((key) => {
      if (String(current[key]).trim()) next[key] = String(Number(current[key]) * multiplier);
      touched.current.add(key);
    });
    touched.current.add('quantity');
    return next;
  };

  const changeListType = (nextType) => {
    if (nextType !== listType && nextType === 'importaciones' && automaticPackSelection.current) {
      automaticPackSelection.current = false;
      setDraft((current) => ({ ...current, purchaseMode: '', purchaseVarietyPriceId: null, metricUnit: '', weight: '' }));
    }
    if (nextType !== listType && nextType !== 'importaciones') {
      automaticPackSelection.current = draft.metricUnit !== 'granel'
        && getPurchasePackFormats(variety.formats || [format]).some((entry) => String(entry.id) === String(format.id));
      setDraft((current) => (
        current.metricUnit === 'granel'
          ? { ...current, purchaseMode: 'granel', purchaseVarietyPriceId: null }
          : selectStockPurchase(current, `pack:${format.id}`, variety.formats || [format])
      ));
    }
    if (!granel && isStandardGranelPurchase({ isImportList: nextType === 'importaciones', metricUnit: draft.metricUnit })) {
      setDraft(asGranelPurchase);
    }
    setError('');
  };

  const update = (field, value) => {
    if (disabled) return;
    touched.current.add(field);
    setDraft((current) => {
      let next = { ...current, [field]: value };
      if (field === 'metricUnit' && !isStockPack(value)) next.unitsPerPack = '';
      if (field === 'metricUnit' && !granel && isStandardGranelPurchase({ isImportList, metricUnit: value })) {
        next = asGranelPurchase(next);
      }
      return next;
    });
    setError('');
  };

  const updatePurchase = (next) => {
    automaticPackSelection.current = false;
    if (next.purchaseMode === 'pack') ['purchaseMode', 'purchaseVarietyPriceId', 'metricUnit', 'weight'].forEach((field) => touched.current.add(field));
    Object.keys(next).forEach((field) => { if (next[field] !== draft[field]) touched.current.add(field); });
    setDraft(next);
    setError('');
  };

  const refreshResult = async (stock) => {
    const result = await onSaved(stock);
    if (validContext()) {
      if (!result) throw new Error('El ingreso quedó guardado, pero falta actualizar el costo. Reintenta la actualización.');
      setRefreshedFormat(result);
    }
  };

  const save = async () => {
    if (operation.current || disabled || loadError || defaultsError || !validContext()) return;
    setError('');
    let payload;
    try {
      if (listId !== NEW_LIST && !selectedList) throw new Error('Selecciona una lista de stock.');
      if (listId === NEW_LIST && !listName.trim()) throw new Error('Ingresa el nombre de la nueva lista.');
      if (isImportList && !canReadProviders) throw new Error('Necesitas el permiso provider.read para seleccionar el proveedor de la importación.');
      if (draft.providerId && !providers.some((provider) => String(provider.id) === String(draft.providerId))) throw new Error('Selecciona un proveedor activo de la lista.');
      if (isImportList && isResaleOnly && !isStockPack(draft.metricUnit) && !saleUnits.some((unit) => unit.label === draft.metricUnit)) throw new Error('Selecciona una unidad de venta para la compra de reventa.');
      payload = buildStockEntryPayload({ draft, listType, variety, isResaleOnly, missingConversions, conversionInputs });
    } catch (failure) { setError(errorMessage(failure)); return; }
    operation.current = true;
    setBusy(true);
    try {
      let destination = selectedList;
      if (listId === NEW_LIST) {
        destination = await createStockList(branchId, { name: listName.trim(), listType, autoActivationScheduledAt: null });
        if (!validContext()) return;
        setLists((current) => [destination, ...current]);
        setListId(String(destination.id));
      }
      if (!validContext()) return;
      const stock = await createStockItem(branchId, { ...payload, stockListId: destination.id });
      if (!validContext()) return;
      setSavedStock(stock);
      await refreshResult(stock);
    } catch (failure) { if (validContext()) setError(errorMessage(failure)); }
    finally { operation.current = false; if (validContext()) setBusy(false); }
  };

  const retryRefresh = async () => {
    if (operation.current || !validContext()) return;
    operation.current = true;
    setBusy(true);
    setError('');
    try { await refreshResult(savedStock); }
    catch (failure) { if (validContext()) setError(errorMessage(failure)); }
    finally { operation.current = false; if (validContext()) setBusy(false); }
  };

  const field = (key, label, options = {}) => {
    const scope = getStockInputScope({ isImportList, purchaseMode: isImportList ? null : draft.purchaseMode, metricUnit: draft.metricUnit }, key);
    return <label className="block text-xs font-semibold text-slate-700" htmlFor={`add-stock-${key}`} key={key}>
      {label}
      <input id={`add-stock-${key}`} type="number" inputMode="decimal" min="0" step="0.001" className={inputClass}
        value={draft[key]} disabled={disabled} onChange={(event) => update(key, event.target.value)} aria-describedby={`add-stock-${key}-help`} {...options} />
      <span id={`add-stock-${key}-help`} className="mt-1 block font-normal text-slate-500">{scope}</span>
    </label>;
  };
  const quantities = calculateStockAmounts({ ...draft, quantity: resolveStandardStockQuantity({ isImportList, purchaseMode: isImportList ? null : draft.purchaseMode, metricUnit: draft.metricUnit, quantity: draft.quantity }) });
  const stockUnits = isImportList && isResaleOnly ? [...new Set([...saleUnits.map((unit) => unit.label), 'pack'])] : [...new Set([...STOCK_UNIT_OPTIONS, ...saleUnits.map((unit) => unit.label), draft.metricUnit].filter(Boolean))];

  return <VarietyEditorDialog title={`${product.name} · ${variety.name}`} eyebrow="Agregar ingreso de stock"
    description="Registra la compra de esta variedad para calcular el costo de sus formatos de venta."
    onClose={onClose} busy={busy} closeLabel="Cerrar ingreso de stock"
    footerNote={savedStock ? 'El ingreso está guardado en la lista de stock.' : 'Los pagos y comprobantes se registran desde la lista de stock.'}
    footerActions={<>
      <button type="button" disabled={busy} onClick={onClose} className="rounded-lg border border-slate-300 px-4 py-2 text-sm">{savedStock ? 'Cerrar' : 'Cancelar'}</button>
      {savedStock ? !refreshedFormat && <button type="button" disabled={busy} onClick={retryRefresh} className="rounded-lg bg-blue-600 px-4 py-2 text-sm text-white">Actualizar costo</button>
        : <button type="button" disabled={disabled || Boolean(loadError || defaultsError)} onClick={save} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50">{busy ? 'Guardando…' : 'Guardar ingreso de stock'}</button>}
    </>}>
    <div className="mx-auto max-w-4xl space-y-4">
      {!canCreateStock && <p role="alert">Necesitas permisos stock.read y stock.create para agregar un ingreso.</p>}
      <StockCostReferenceSummary costs={initialCosts} formatLabel={format.productFormatName} />
      {savedStock ? <section ref={resultRef} tabIndex={-1} aria-live="polite" className="space-y-3 rounded-xl border border-emerald-300 bg-emerald-50 p-4 outline-none focus-visible:ring-2 focus-visible:ring-emerald-500">
        <h3 className="font-semibold text-emerald-950">Ingreso de stock guardado</h3>
        <p className="text-sm">{selectedList?.name} · Registro #{savedStock.id}</p>
        {refreshedFormat && <>
          <StockCostReferenceSummary costs={refreshedFormat.costs || {}} formatLabel={format.productFormatName} title="Costo después del ingreso" />
          <p className="text-sm">Fuente del cálculo: {refreshedFormat.costs?.sourceStockListName || 'Sin fuente disponible'}</p>
          {(costNumber(refreshedFormat.costs?.referenceCost) === null || refreshedFormat.costs?.calculatedCost === null
            || String(refreshedFormat.costs?.conversionStatus || '').startsWith('missing_') || refreshedFormat.costs?.conversionStatus === 'unit_not_in_import')
            && <p className="text-sm text-black">{costAvailabilityMessage(refreshedFormat.costs)}</p>}
        </>}
      </section> : <>
        {loading && <p role="status">Cargando listas y proveedores…</p>}
        {loadError && <div role="alert" className="text-sm text-red-700">{loadError} <button type="button" onClick={() => setReload((value) => value + 1)} className="underline">Reintentar carga</button></div>}
        <section className="space-y-3 rounded-xl border border-slate-200 p-4" aria-label="Lista de destino">
          <label htmlFor="add-stock-list" className="block text-sm font-semibold">Lista de stock
            <select id="add-stock-list" value={listId} onChange={(event) => {
              const value = event.target.value;
              changeListType(value === NEW_LIST ? newListType : lists.find((list) => String(list.id) === value)?.listType || 'standard');
              setListId(value);
            }} disabled={disabled} className={inputClass}>
              <option value="">Selecciona una lista</option>
              {lists.map((list) => <option key={list.id} value={list.id}>{listLabel(list)}</option>)}
              <option value={NEW_LIST}>Crear nueva lista</option>
            </select>
          </label>
          {listId === NEW_LIST && <div className="grid gap-3 sm:grid-cols-2">
            <label htmlFor="add-stock-list-name" className="text-sm">Nombre de la nueva lista<input id="add-stock-list-name" value={listName} disabled={disabled} onChange={(event) => setListName(event.target.value)} className={inputClass} /></label>
            <label htmlFor="add-stock-list-type" className="text-sm">Tipo de lista<select id="add-stock-list-type" value={newListType} disabled={disabled} onChange={(event) => { changeListType(event.target.value); setNewListType(event.target.value); }} className={inputClass}><option value="standard">Normal</option><option value="importaciones">Importaciones</option></select></label>
            <p className="text-xs text-slate-500 sm:col-span-2">La nueva lista se guardará como borrador, sin activación programada.</p>
          </div>}
          {selectedList && <p className="text-xs text-slate-600">{listLabel(selectedList)}</p>}
          <p className="text-xs text-slate-600">Los gastos de la lista se distribuyen entre sus compras. Agregar un ingreso puede cambiar los gastos asignados y los costos de otras variedades.</p>
        </section>
        {loadingDefaults && <p role="status">Cargando datos de importación…</p>}
        {defaultsError && <div role="alert" className="text-sm text-red-700">{defaultsError} <button type="button" className="underline" onClick={() => setDefaultsReload((value) => value + 1)}>Reintentar datos de importación</button></div>}
        <section className="space-y-4 rounded-xl border border-slate-200 p-4" aria-label="Datos de compra">
          <h3 className="font-semibold">Compra de {variety.name}</h3>
          {!isImportList && <StockPurchaseFormatSelector branchId={branchId} formats={variety.formats || [format]} draft={draft} idPrefix="add-stock" disabled={disabled} onChange={updatePurchase} />}
          {isImportList && <label htmlFor="add-stock-metricUnit" className="block text-xs font-semibold">Unidad de compra
            <select id="add-stock-metricUnit" value={draft.metricUnit} disabled={disabled} onChange={(event) => update('metricUnit', event.target.value)} className={inputClass} aria-describedby="add-stock-unit-help">
              <option value="">Selecciona una unidad</option>{stockUnits.map((unit) => <option key={unit} value={unit}>{isStockPack(unit) ? 'Pack de X unidades' : stockPurchaseUnitLabel(unit)}</option>)}
            </select>
            <span id="add-stock-unit-help" className="mt-1 block font-normal text-slate-500">{getStockInputScope({}, 'metricUnit')}</span>
          </label>}
          <div className="grid gap-4 sm:grid-cols-2">
            {field('price', 'Monto comprado con IVA', { step: '1', inputMode: 'numeric' })}
            {field('priceWithoutIva', 'Monto comprado sin IVA', { step: '1', inputMode: 'numeric' })}
            {!granel && field('quantity', !isImportList ? 'Cantidad de formatos' : isStockPack(draft.metricUnit) ? 'Cantidad de packs' : 'Cantidad', { min: '0.001' })}
            {isImportList && isStockPack(draft.metricUnit) && field('unitsPerPack', 'Unidades por pack', { min: '1', step: '1', inputMode: 'numeric', placeholder: 'Ej.: 12 para una docena' })}
            {isImportList && field('weight', guidance?.weightLabel || 'Peso por unidad de compra (kg)', { min: draft.metricUnit === 'bin' && !isImportList ? '1' : '0.001', step: draft.metricUnit === 'bin' && !isImportList ? '1' : '0.001' })}
            {isImportList && draft.metricUnit.toLowerCase() === 'bin' && field('bultosPerBin', 'Bultos por bin', { min: '1', step: '1', inputMode: 'numeric' })}
            {isImportList && ['lengthCm', 'widthCm', 'heightCm'].map((key, index) => field(key, ['Largo (cm)', 'Ancho (cm)', 'Alto (cm)'][index], { min: '0.001' }))}
          </div>
          {isImportList && isStockPack(draft.metricUnit) && <p aria-live="polite" className="text-sm text-slate-600">{stockPackTotal(draft)}</p>}
          <StockCostPerKgPreview draft={draft} isImportList={isImportList} />
          <div className="space-y-1 rounded-lg bg-slate-50 p-3 text-xs text-black"><p>{STOCK_AMOUNT_GUIDANCE}</p>{guidance && <><p>{guidance.amount}</p><p>{guidance.weight}</p></>}</div>
          <dl className="grid gap-3 text-sm sm:grid-cols-2"><div><dt>Total de esta compra al proveedor</dt><dd className="font-semibold">{referenceMoney(quantities.providerTotalAmount)}</dd></div><div><dt>Costo total de esta compra, antes de gastos</dt><dd className="font-semibold">{referenceMoney(quantities.stockCostAmount)}</dd></div></dl>
          <label htmlFor="add-stock-providerId" className="block text-sm font-semibold">Proveedor{!isImportList ? ' (opcional)' : ''}
            <select id="add-stock-providerId" value={draft.providerId} disabled={disabled || !canReadProviders || Boolean(providerError)} onChange={(event) => update('providerId', event.target.value)} className={inputClass}>
              <option value="">Selecciona un proveedor</option>{providers.map((provider) => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
            </select>
          </label>
          {!canReadProviders && <p className="text-xs text-slate-600">Necesitas el permiso provider.read para seleccionar un proveedor{isImportList ? ' y registrar esta importación' : ''}.</p>}
          {providerError && <p role="alert" className="text-sm text-red-700">{providerError} <button type="button" disabled={busy} onClick={() => setReload((value) => value + 1)} className="underline">Reintentar proveedores</button></p>}
          {isImportList && isResaleOnly && <p className="text-xs text-slate-500">En reventa, el peso y las dimensiones son opcionales; puedes comprar en una unidad de venta existente o en packs de unidades.</p>}
        </section>
        {missingConversions.length > 0 && <section className="space-y-3 rounded-xl border border-amber-200 bg-amber-50 p-4" aria-label="Conversiones requeridas">
          <h3 className="font-semibold">Equivalencias entre compra y venta</h3>
          {missingConversions.map((unit) => <div key={unit.key} className="grid grid-cols-[1fr_auto_1fr] items-center gap-3">
            {['fromAmount', 'toAmount'].map((key, index) => <React.Fragment key={key}>{index === 1 && <span aria-hidden="true">=</span>}<label className="text-xs" htmlFor={`add-stock-conversion-${unit.key}-${key}`}>{index === 0 ? `${isStockPack(draft.metricUnit) ? 'unidad' : draft.metricUnit} de compra` : `${unit.label} de venta`}
              <input id={`add-stock-conversion-${unit.key}-${key}`} type="number" min="0.001" step="0.001" disabled={disabled} className={inputClass} value={conversionInputs[unit.key]?.[key] ?? (index === 0 ? '1' : '')} onChange={(event) => setConversionInputs((current) => ({ ...current, [unit.key]: { ...current[unit.key], [key]: event.target.value } }))} />
            </label></React.Fragment>)}
          </div>)}
        </section>}
      </>}
      {error && <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
    </div>
  </VarietyEditorDialog>;
}
